import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import CustomDropdown from '../../../CustomDropdown';
import OrderDesign from '../../../OrderDesign';
import { API_URL } from '../../../config/apiConfig';
import {
  SPOTTING_OBJECTS_ORDER_DISPLAY_SCALE,
  SPOTTING_OBJECTS_MAGNIFIER_ZOOM,
  SPOTTING_OBJECTS_MAGNIFIER_RADIUS_PX,
} from './spottingObjectsLayout';
import {
  buildSpottingObjectsFactionDropdownOptions,
  getSpottingObjectsFactionIconPath,
  getSpottingObjectsFactionOrbBackground,
} from './spottingObjectsFactionUi';
import SpottingObjectsSceneContent from './SpottingObjectsSceneContent';
import {
  buildMagnifierZoomTransform,
  clientPointToArenaLocal,
  isSpottingObjectsMagnifierExcluded,
} from './spottingObjectsMagnifier';
import './SpottingObjectsMinigame.css';

/**
 * @param {object} props
 * @param {{ orderId: number, orderDisplayScale?: number, factionOptions: string[] }} props.challenge
 * @param {(pickedTreeIndex: number) => void} props.onPickTree
 * @param {boolean} props.disabled
 * @param {React.RefObject<HTMLElement>} props.containerRef
 * @param {null | 'won' | 'lost'} props.resultOutcome
 * @param {(() => void) | undefined} props.onResultProceed
 * @param {{ id: string } | null} props.winReward
 */
export default function SpottingObjectsMinigame({
  challenge,
  onPickTree,
  disabled = false,
  containerRef,
  resultOutcome = null,
  onResultProceed,
  winReward = null,
}) {
  const { t, i18n } = useTranslation();
  const playArenaRef = useRef(null);
  const submittedRef = useRef(false);

  const orderId = challenge.orderId;
  const orderDisplayScale = challenge.orderDisplayScale ?? SPOTTING_OBJECTS_ORDER_DISPLAY_SCALE;
  /** Config uses 0.01 (1%); scale up for the lens so the card is visible on screen. */
  const lensOrderScale = orderDisplayScale * 9;
  const factionOptions = challenge.factionOptions;

  const [order, setOrder] = useState(null);
  const [orderLoading, setOrderLoading] = useState(false);
  const [orderLoadError, setOrderLoadError] = useState(null);
  const [selectedFactionIndex, setSelectedFactionIndex] = useState(null);
  const [magnifierPoint, setMagnifierPoint] = useState(null);
  const [magnifierRadiusPx, setMagnifierRadiusPx] = useState(SPOTTING_OBJECTS_MAGNIFIER_RADIUS_PX);
  const magnifierEnabled = resultOutcome == null && !disabled;

  const dropdownOptions = useMemo(
    () => buildSpottingObjectsFactionDropdownOptions(factionOptions),
    [factionOptions]
  );

  const winRewardBodyKey =
    resultOutcome === 'won' && winReward && typeof winReward.id === 'string'
      ? `exploreUnknownPage.minigame.spottingObjects.winReward.${winReward.id}`
      : null;

  const setPlayArenaRef = useCallback(
    (node) => {
      playArenaRef.current = node;
      if (containerRef && typeof containerRef === 'object') {
        containerRef.current = node;
      }
    },
    [containerRef]
  );

  const fetchOrderFull = useCallback(
    async (id) => {
      const numericId = Number(id);
      if (Number.isNaN(numericId)) {
        throw new Error(`SpottingObjectsMinigame: invalid orderId (${String(id)})`);
      }
      const authToken = localStorage.getItem('token');
      const uiLang = encodeURIComponent(i18n.resolvedLanguage || i18n.language || 'en-us');
      const response = await fetch(`${API_URL}/order/${numericId}/full?lang=${uiLang}`, {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch order ${numericId} (status ${response.status})`);
      }
      return response.json();
    },
    [i18n.resolvedLanguage, i18n.language]
  );

  useEffect(() => {
    if (!Number.isInteger(orderId)) {
      return undefined;
    }
    let cancelled = false;
    setOrderLoading(true);
    setOrderLoadError(null);
    fetchOrderFull(orderId)
      .then((data) => {
        if (!cancelled) {
          setOrder(data);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setOrderLoadError(err instanceof Error ? err.message : String(err));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setOrderLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [orderId, fetchOrderFull]);

  const syncMagnifierRadius = useCallback(() => {
    const el = playArenaRef.current;
    if (!el) {
      return;
    }
    const w = el.clientWidth;
    if (!(w > 0)) {
      return;
    }
    setMagnifierRadiusPx((SPOTTING_OBJECTS_MAGNIFIER_RADIUS_PX * w) / 1920);
  }, []);

  useEffect(() => {
    const el = playArenaRef.current;
    if (!el || !magnifierEnabled) {
      return undefined;
    }
    syncMagnifierRadius();
    window.addEventListener('resize', syncMagnifierRadius);
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(syncMagnifierRadius);
      ro.observe(el);
    }
    return () => {
      window.removeEventListener('resize', syncMagnifierRadius);
      if (ro) {
        ro.disconnect();
      }
    };
  }, [magnifierEnabled, syncMagnifierRadius]);

  const updateMagnifierFromEvent = useCallback(
    (e) => {
      if (!magnifierEnabled) {
        setMagnifierPoint(null);
        return;
      }
      if (isSpottingObjectsMagnifierExcluded(e.target, e)) {
        setMagnifierPoint(null);
        return;
      }
      const el = playArenaRef.current;
      if (!el) {
        return;
      }
      const local = clientPointToArenaLocal(e.clientX, e.clientY, el.getBoundingClientRect());
      setMagnifierPoint(local);
    },
    [magnifierEnabled]
  );

  const handleArenaMouseMove = useCallback(
    (e) => {
      updateMagnifierFromEvent(e);
    },
    [updateMagnifierFromEvent]
  );

  const handleArenaMouseLeave = useCallback(() => {
    setMagnifierPoint(null);
  }, []);

  const sceneContentProps = { order, orderLoading, lensOrderScale };

  const renderFactionOption = useCallback((option) => {
    if (!option?.factionName) {
      return option?.label || '';
    }
    const iconPath = getSpottingObjectsFactionIconPath(option.factionName);
    return (
      <div className="spotting-objects-faction-option">
        <div
          className="faction-orb"
          style={{ background: getSpottingObjectsFactionOrbBackground(option.factionName) }}
        >
          {iconPath ? (
            <img
              src={iconPath}
              alt=""
              className="faction-orb-icon"
              draggable={false}
              aria-hidden="true"
            />
          ) : null}
        </div>
        <span>{option.label}</span>
      </div>
    );
  }, []);

  const handleSubmitGuess = useCallback(() => {
    if (submittedRef.current || disabled || resultOutcome != null) {
      return;
    }
    if (!Number.isInteger(selectedFactionIndex)) {
      return;
    }
    submittedRef.current = true;
    onPickTree(selectedFactionIndex);
  }, [disabled, onPickTree, resultOutcome, selectedFactionIndex]);

  const canSubmit =
    Number.isInteger(selectedFactionIndex) &&
    !disabled &&
    resultOutcome == null &&
    !orderLoading &&
    orderLoadError == null;

  return (
    <div className="spotting-objects-minigame">
      <div
        ref={setPlayArenaRef}
        className={
          magnifierEnabled
            ? 'spotting-objects-play-arena spotting-objects-play-arena--magnifier'
            : 'spotting-objects-play-arena'
        }
        aria-label={t('exploreUnknownPage.minigame.spottingObjects.playArenaAria')}
        onMouseMove={handleArenaMouseMove}
        onMouseLeave={handleArenaMouseLeave}
      >
        <div className="spotting-objects-scene-base" aria-hidden="true">
          <SpottingObjectsSceneContent {...sceneContentProps} />
        </div>

        {magnifierEnabled && magnifierPoint ? (
          <>
            <div
              className="spotting-objects-magnifier-layer"
              aria-hidden="true"
              style={{
                clipPath: `circle(${magnifierRadiusPx}px at ${magnifierPoint.x}px ${magnifierPoint.y}px)`,
              }}
            >
              <div
                className="spotting-objects-magnifier-zoom"
                style={{
                  transform: buildMagnifierZoomTransform(magnifierPoint, SPOTTING_OBJECTS_MAGNIFIER_ZOOM),
                }}
              >
                <SpottingObjectsSceneContent {...sceneContentProps} />
              </div>
            </div>
            <div
              className="spotting-objects-cursor-disc"
              aria-hidden="true"
              style={{
                width: magnifierRadiusPx * 2,
                height: magnifierRadiusPx * 2,
                left: magnifierPoint.x - magnifierRadiusPx,
                top: magnifierPoint.y - magnifierRadiusPx,
              }}
            />
          </>
        ) : null}

        {orderLoadError ? (
          <p className="spotting-objects-load-error" role="alert">
            {orderLoadError}
          </p>
        ) : null}

        <div className="spotting-objects-hud spotting-objects-hud--top" data-no-minigame-flashlight="true">
          <h3 id="explore-unknown-minigame-title" className="spotting-objects-title">
            {t('exploreUnknownPage.minigame.spottingObjects.title')}
          </h3>
          {resultOutcome == null ? (
            <p className="spotting-objects-prompt">{t('exploreUnknownPage.minigame.spottingObjects.prompt')}</p>
          ) : null}
        </div>

        {resultOutcome == null ? (
          <div
            className="spotting-objects-controls"
            data-no-minigame-flashlight="true"
            data-spotting-magnifier-exclude="true"
          >
            <label className="spotting-objects-faction-label" htmlFor="spotting-objects-faction-select">
              {t('exploreUnknownPage.minigame.spottingObjects.factionLabel')}
            </label>
            <div className="spotting-objects-faction-dropdown" id="spotting-objects-faction-select">
              <CustomDropdown
                options={dropdownOptions}
                value={selectedFactionIndex}
                onChange={(value) => setSelectedFactionIndex(value)}
                placeholder={t('exploreUnknownPage.minigame.spottingObjects.factionPlaceholder')}
                renderOption={renderFactionOption}
                openUpward
                disabled={disabled || orderLoading || orderLoadError != null}
              />
            </div>
            <button
              type="button"
              className="spotting-objects-submit"
              disabled={!canSubmit}
              onClick={handleSubmitGuess}
            >
              {t('exploreUnknownPage.minigame.spottingObjects.submitGuess')}
            </button>
          </div>
        ) : null}

        {resultOutcome != null ? (
          <div
            className={`spotting-objects-hud spotting-objects-hud--bottom spotting-objects-result-banner spotting-objects-result-banner--${resultOutcome}`}
            role="status"
            aria-live="polite"
            data-no-minigame-flashlight="true"
          >
            <p className="spotting-objects-result-banner-title">
              {resultOutcome === 'won'
                ? t('exploreUnknownPage.minigame.spottingObjects.successTitle')
                : t('exploreUnknownPage.minigame.spottingObjects.failureTitle')}
            </p>
            {resultOutcome === 'won' ? (
              <p className="spotting-objects-result-banner-body">
                {winRewardBodyKey
                  ? t(winRewardBodyKey, {
                      percent: winReward.darknessReductionApplied,
                      xp: winReward.xpGained,
                      billyBucks: winReward.billyBucksAmount,
                      health: winReward.startHealthBonus != null ? winReward.startHealthBonus : 3,
                    })
                  : t('exploreUnknownPage.minigame.spottingObjects.successBodyGeneric')}
              </p>
            ) : (
              <p className="spotting-objects-result-banner-body">
                {t('exploreUnknownPage.minigame.spottingObjects.failureBody')}
              </p>
            )}
            {typeof onResultProceed === 'function' ? (
              <button
                type="button"
                className={
                  resultOutcome === 'lost'
                    ? 'spotting-objects-result-proceed spotting-objects-result-proceed--lost'
                    : 'spotting-objects-result-proceed'
                }
                onClick={() => onResultProceed()}
              >
                {t('exploreUnknownPage.minigame.proceed')}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
